import React, { useEffect, useState } from 'react';
import { CountrySelector } from './CountrySelector';
import { CurrencySelector } from './CurrencySelector';
import { ContactList } from './ContactList';
import { AddressList } from './AddressList';
import { ContactForm } from './ContactForm';
import { AddressForm } from './AddressForm';
import { LogoManagement, LogoAsset } from './LogoManagement';
import { PlusIcon, CheckIcon, XIcon, SaveIcon } from 'lucide-react';
export function EntityDetail({
  entity,
  onSave,
  onCancel,
  onSelectSite,
  onCreateSite,
  locale
}) {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    code: '',
    country: '',
    currency: '',
    activeFrom: '',
    activeTo: '',
    isActive: true,
    sites: [],
    contacts: [],
    addresses: []
  });
  const [activeTab, setActiveTab] = useState('details');
  const [showContactForm, setShowContactForm] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null);
  const [logos, setLogos] = useState<LogoAsset[]>([{
    id: 'logo-001',
    scope: 'Tenant',
    fileName: 'company-logo.png',
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/wDZe3XuJbKbBWqS4Gjsurv/Rapid_P2P_-_Blue-Green.svg',
    contentType: 'image/png',
    uploadedAt: '2023-01-15T10:30:00Z',
    uploadedByUserId: 'user-001',
    uploadedByUserName: 'Admin User',
    isActive: true
  }]);
  useEffect(() => {
    if (entity) {
      setFormData({
        ...entity,
        sites: entity.sites || [],
        contacts: entity.contacts || [],
        addresses: entity.addresses || []
      });
    } else {
      // Default values for new entity
      setFormData({
        id: '',
        name: '',
        code: '',
        country: '',
        currency: '',
        activeFrom: new Date().toISOString().split('T')[0],
        activeTo: '',
        isActive: true,
        sites: [],
        contacts: [],
        addresses: []
      });
    }
  }, [entity]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleCountryChange = countryCode => {
    setFormData({
      ...formData,
      country: countryCode
    });
  };
  const handleCurrencyChange = currencyCode => {
    setFormData({
      ...formData,
      currency: currencyCode
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    onSave(formData);
  };
  const handleAddContact = () => {
    setEditingContact(null);
    setShowContactForm(true);
  };
  const handleEditContact = contact => {
    setEditingContact(contact);
    setShowContactForm(true);
  };
  const handleSaveContact = contactData => {
    if (contactData.id) {
      // Update existing contact
      setFormData({
        ...formData,
        contacts: formData.contacts.map(contact => contact.id === contactData.id ? contactData : contact)
      });
    } else {
      // Add new contact
      const newContact = {
        ...contactData,
        id: `con-${new Date().getTime()}`
      };
      setFormData({
        ...formData,
        contacts: [...formData.contacts, newContact]
      });
    }
    setShowContactForm(false);
  };
  const handleDeleteContact = contactId => {
    setFormData({
      ...formData,
      contacts: formData.contacts.filter(contact => contact.id !== contactId)
    });
  };
  const handleAddAddress = () => {
    setEditingAddress(null);
    setShowAddressForm(true);
  };
  const handleEditAddress = address => {
    setEditingAddress(address);
    setShowAddressForm(true);
  };
  const handleSaveAddress = addressData => {
    if (addressData.id) {
      // Update existing address
      setFormData({
        ...formData,
        addresses: formData.addresses.map(address => address.id === addressData.id ? addressData : address)
      });
    } else {
      // Add new address
      const newAddress = {
        ...addressData,
        id: `addr-${new Date().getTime()}`
      };
      setFormData({
        ...formData,
        addresses: [...formData.addresses, newAddress]
      });
    }
    setShowAddressForm(false);
  };
  const handleDeleteAddress = addressId => {
    setFormData({
      ...formData,
      addresses: formData.addresses.filter(address => address.id !== addressId)
    });
  };
  // Logo management handlers
  const handleLogoUpload = (file: File, scope: 'Tenant' | 'Entity' | 'Site', entityId?: string, siteId?: string) => {
    // In a real app, this would upload to a server
    // Here we'll create a local URL for demo purposes
    const reader = new FileReader();
    reader.onload = () => {
      const newLogo: LogoAsset = {
        id: `logo-${new Date().getTime()}`,
        scope,
        entityId: entityId,
        entityName: entityId ? formData.name : undefined,
        siteId,
        siteName: undefined,
        fileName: file.name,
        url: reader.result as string,
        contentType: file.type,
        uploadedAt: new Date().toISOString(),
        uploadedByUserId: 'user-001',
        uploadedByUserName: 'Admin User',
        isActive: true
      };
      // Deactivate other logos with the same scope and entity/site
      const updatedLogos = logos.map(logo => {
        if (logo.scope === scope && (scope === 'Tenant' || scope === 'Entity' && logo.entityId === entityId || scope === 'Site' && logo.siteId === siteId)) {
          return {
            ...logo,
            isActive: false
          };
        }
        return logo;
      });
      setLogos([...updatedLogos, newLogo]);
    };
    reader.readAsDataURL(file);
  };
  const handleLogoDelete = (logoId: string) => {
    setLogos(logos.filter(logo => logo.id !== logoId));
  };
  const handleSetActiveLogo = (logoId: string) => {
    const logoToActivate = logos.find(logo => logo.id === logoId);
    if (!logoToActivate) return;
    const updatedLogos = logos.map(logo => {
      if (logo.scope === logoToActivate.scope && (logo.scope === 'Tenant' || logo.scope === 'Entity' && logo.entityId === logoToActivate.entityId || logo.scope === 'Site' && logo.siteId === logoToActivate.siteId)) {
        return {
          ...logo,
          isActive: logo.id === logoId
        };
      }
      return logo;
    });
    setLogos(updatedLogos);
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-15320">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-15321">
        <div data-id="element-15322">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15323">
            {entity ? 'Edit Entity' : 'Create Entity'}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-15324">
            {entity ? 'Update entity details' : 'Enter details for the new entity'}
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-15325">
          <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15326">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15327">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-15328" />
            Save Entity
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-15329">
        <nav className="-mb-px flex" aria-label="Tabs" data-id="element-15330">
          <button className={`${activeTab === 'details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} w-1/5 py-4 px-1 text-center border-b-2 font-medium text-sm`} onClick={() => setActiveTab('details')} data-id="element-15331">
            Details
          </button>
          <button className={`${activeTab === 'sites' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} w-1/5 py-4 px-1 text-center border-b-2 font-medium text-sm`} onClick={() => setActiveTab('sites')} data-id="element-15332">
            Sites
          </button>
          <button className={`${activeTab === 'contacts' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} w-1/5 py-4 px-1 text-center border-b-2 font-medium text-sm`} onClick={() => setActiveTab('contacts')} data-id="element-15333">
            Contacts
          </button>
          <button className={`${activeTab === 'addresses' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} w-1/5 py-4 px-1 text-center border-b-2 font-medium text-sm`} onClick={() => setActiveTab('addresses')} data-id="element-15334">
            Addresses
          </button>
          <button className={`${activeTab === 'logo' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} w-1/5 py-4 px-1 text-center border-b-2 font-medium text-sm`} onClick={() => setActiveTab('logo')} data-id="element-15335">
            Logo
          </button>
        </nav>
      </div>

      {/* Details Tab */}
      {activeTab === 'details' && <div className="p-4 sm:p-6" data-id="element-15336">
          <form className="space-y-6" data-id="element-15337">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-15338">
              <div className="sm:col-span-3" data-id="element-15339">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-15340">
                  Entity Name
                </label>
                <div className="mt-1" data-id="element-15341">
                  <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15342" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-15343">
                <label htmlFor="code" className="block text-sm font-medium text-gray-700" data-id="element-15344">
                  Entity Code
                </label>
                <div className="mt-1" data-id="element-15345">
                  <input type="text" name="code" id="code" value={formData.code} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15346" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-15347">
                <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-15348">
                  Country
                </label>
                <div className="mt-1" data-id="element-15349">
                  <CountrySelector value={formData.country} onChange={handleCountryChange} locale={locale} data-id="element-15350" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-15351">
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-15352">
                  Default Currency
                </label>
                <div className="mt-1" data-id="element-15353">
                  <CurrencySelector value={formData.currency} onChange={handleCurrencyChange} countryCode={formData.country} locale={locale} data-id="element-15354" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-15355">
                <label htmlFor="activeFrom" className="block text-sm font-medium text-gray-700" data-id="element-15356">
                  Active From
                </label>
                <div className="mt-1" data-id="element-15357">
                  <input type="date" name="activeFrom" id="activeFrom" value={formData.activeFrom} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15358" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-15359">
                <label htmlFor="activeTo" className="block text-sm font-medium text-gray-700" data-id="element-15360">
                  Active To
                </label>
                <div className="mt-1" data-id="element-15361">
                  <input type="date" name="activeTo" id="activeTo" value={formData.activeTo} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15362" />
                </div>
              </div>
              <div className="sm:col-span-6" data-id="element-15363">
                <div className="flex items-center" data-id="element-15364">
                  <input id="isActive" name="isActive" type="checkbox" checked={formData.isActive} onChange={handleChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-15365" />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700" data-id="element-15366">
                    Is Active
                  </label>
                </div>
              </div>
            </div>
          </form>
        </div>}

      {/* Sites Tab */}
      {activeTab === 'sites' && <div className="p-4 sm:p-6" data-id="element-15367">
          <div className="mb-4 flex justify-end" data-id="element-15368">
            <button type="button" onClick={onCreateSite} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15369">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-15370" />
              Add Site
            </button>
          </div>
          {formData.id ? <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-15371">
              <ul className="divide-y divide-gray-200" data-id="element-15372">
                {formData.sites.map(site => <li key={site.id} data-id="element-15373">
                    <button onClick={() => onSelectSite(site)} className="w-full text-left hover:bg-gray-50 px-4 py-4 sm:px-6 flex items-center justify-between" data-id="element-15374">
                      <div data-id="element-15375">
                        <div className="flex items-center" data-id="element-15376">
                          <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-15377">
                            {site.name}
                          </p>
                          <p className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800" data-id="element-15378">
                            {site.code}
                          </p>
                          {site.isActive ? <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-15379">
                              <CheckIcon className="h-3 w-3 mr-1" data-id="element-15380" />
                              Active
                            </span> : <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800" data-id="element-15381">
                              <XIcon className="h-3 w-3 mr-1" data-id="element-15382" />
                              Inactive
                            </span>}
                        </div>
                        <div className="mt-2 sm:flex sm:justify-between" data-id="element-15383">
                          <div className="sm:flex" data-id="element-15384">
                            <p className="flex items-center text-sm text-gray-500" data-id="element-15385">
                              {site.country}
                            </p>
                            <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6" data-id="element-15386">
                              {site.currency}
                            </p>
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>)}
                {formData.sites.length === 0 && <li className="px-4 py-5 sm:px-6 text-center text-sm text-gray-500" data-id="element-15387">
                    No sites found. Click "Add Site" to create one.
                  </li>}
              </ul>
            </div> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-15388">
              Please save the entity first before adding sites.
            </div>}
        </div>}

      {/* Contacts Tab */}
      {activeTab === 'contacts' && <div className="p-4 sm:p-6" data-id="element-15389">
          {showContactForm ? <ContactForm contact={editingContact} onSave={handleSaveContact} onCancel={() => setShowContactForm(false)} locale={locale} data-id="element-15390" /> : <>
              <div className="mb-4 flex justify-end" data-id="element-15391">
                <button type="button" onClick={handleAddContact} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15392">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-15393" />
                  Add Contact
                </button>
              </div>
              <ContactList contacts={formData.contacts} onEdit={handleEditContact} onDelete={handleDeleteContact} locale={locale} data-id="element-15394" />
            </>}
        </div>}

      {/* Addresses Tab */}
      {activeTab === 'addresses' && <div className="p-4 sm:p-6" data-id="element-15395">
          {showAddressForm ? <AddressForm address={editingAddress} onSave={handleSaveAddress} onCancel={() => setShowAddressForm(false)} locale={locale} data-id="element-15396" /> : <>
              <div className="mb-4 flex justify-end" data-id="element-15397">
                <button type="button" onClick={handleAddAddress} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15398">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-15399" />
                  Add Address
                </button>
              </div>
              <AddressList addresses={formData.addresses} onEdit={handleEditAddress} onDelete={handleDeleteAddress} locale={locale} data-id="element-15400" />
            </>}
        </div>}

      {/* Logo Tab */}
      {activeTab === 'logo' && <div className="p-4 sm:p-6" data-id="element-15401">
          <LogoManagement scope="Entity" entityId={formData.id} entityName={formData.name} logos={logos} onUpload={handleLogoUpload} onDelete={handleLogoDelete} onSetActive={handleSetActiveLogo} locale={locale} data-id="element-15402" />
        </div>}
    </div>;
}